import {
  effect,
  exhaustMapWithTrailing,
  fromPromise,
  LiveData,
  Service,
} from '@toeverything/infra';
import { combineLatest, map, Observable, type Subscription, tap } from 'rxjs';

import type { WorkspaceService } from '../../workspace';
import type {
  DocPermissionActions,
  GuardStore,
  WorkspacePermissionActions,
} from '../stores/guard';
import { workspacePermissionEventMayChangeRole } from '../utils/workspace-permission-events';
import type { WorkspacePermissionService } from './permission';

export class GuardService extends Service {
  private readonly permissionsChangedSubscription?: Subscription;
  private readonly docPermissionLoads = new Map<
    string,
    Promise<Record<DocPermissionActions, boolean>>
  >();

  constructor(
    private readonly guardStore: GuardStore,
    private readonly workspaceService: WorkspaceService,
    private readonly workspacePermissionService: WorkspacePermissionService
  ) {
    super();
    this.permissionsChangedSubscription = this.subscribeToPermissionEvents();
    void this.initialize();
  }

  private async initialize(): Promise<void> {
    await this.guardStore.hydrateLocalPermissionSnapshot();
    if (
      this.workspaceService.workspace.flavour !== 'local' &&
      !this.workspaceService.workspace.openOptions.isSharedMode
    ) {
      await this.loadWorkspacePermission().catch(() => undefined);
    }
  }

  private subscribeToPermissionEvents() {
    const permissionsChanged$ =
      this.workspaceService.workspace.engine.permissionsChanged$;
    if (!permissionsChanged$) {
      return undefined;
    }

    return permissionsChanged$.subscribe(event => {
      this.guardStore.observePermissionGraphVersion(
        event.permissionGraphVersion
      );
      if (workspacePermissionEventMayChangeRole(event.changeType)) {
        this.revalidateCachedPermissions({ forceWorkspace: true });
      }
      if (event.affectedDocIds?.length) {
        this.revalidateCachedPermissions({ docIds: event.affectedDocIds });
      }
    });
  }

  private readonly workspacePermissions$ = new LiveData<
    Partial<Record<WorkspacePermissionActions, boolean>>
  >({});

  private readonly docPermissions$ = new LiveData<
    Record<string, Partial<Record<DocPermissionActions, boolean>>>
  >({});

  private readonly isOwner$ = LiveData.computed(get => {
    const isOwner = get(this.workspacePermissionService.permission.isOwner$);
    return isOwner ?? null;
  });

  /**
   * @example
   * ```ts
   * guardService.can$('Workspace_Properties_Update');
   * guardService.can$('Doc_Update', docId);
   * ```
   *
   * @returns LiveData<boolean | undefined> the value is undefined if the permission is loading
   */
  can$<T extends WorkspacePermissionActions | DocPermissionActions>(
    action: T,
    ...args: T extends DocPermissionActions ? [string] : []
  ): LiveData<boolean | undefined> {
    const docId = args[0];
    if (this.workspaceService.workspace.flavour === 'local') {
      return new LiveData<boolean | undefined>(true);
    }
    return LiveData.from(
      new Observable(subscriber => {
        let prev: boolean | undefined = undefined;

        const subscription = combineLatest([
          (docId
            ? combineLatest([
                this.docPermissions$.pipe(
                  map(permissions => permissions[docId] ?? {})
                ),
                this.guardStore.watchCachedDocPermissions(docId).asObservable(),
              ]).pipe(
                map(([online, cached]) => ({
                  ...online,
                  ...(cached ?? {}),
                }))
              )
            : this.workspacePermissions$.asObservable()) as Observable<
            Record<string, boolean>
          >,
          this.isOwner$,
        ]).subscribe(([permissions, isOwner]) => {
          if (!docId && isOwner) {
            return subscriber.next(true);
          }
          const current = permissions[action] ?? undefined;
          // While workspace role is still loading (null), don't emit
          // a definitive workspace false — the user might be Owner.
          // For doc permissions, a loaded doc-specific cache value is already
          // the backend-computed answer and can be used while offline.
          if (isOwner === null) {
            if (docId) {
              if (current !== prev) {
                prev = current;
                subscriber.next(current);
              }
              return;
            }
            if (prev !== undefined) {
              prev = undefined;
              subscriber.next(undefined);
            }
            return;
          }
          if (current !== prev) {
            prev = current;
            subscriber.next(current);
          }
        });

        if (
          docId &&
          this.workspaceService.workspace.flavour !== 'local' &&
          !this.workspaceService.workspace.openOptions.isSharedMode
        ) {
          void this.loadDocPermission(docId).catch(() => undefined);
        }

        return () => {
          subscription.unsubscribe();
        };
      }),
      undefined
    );
  }

  async can<T extends WorkspacePermissionActions | DocPermissionActions>(
    action: T,
    ...args: T extends DocPermissionActions ? [string] : []
  ): Promise<boolean> {
    const docId = args[0];
    if (this.workspaceService.workspace.flavour === 'local') {
      return true;
    }

    if (docId) {
      const permissions = await this.loadDocPermission(docId);
      return permissions[action as keyof typeof permissions] ?? false;
    }

    if (this.isOwner$.value === null) {
      await this.workspacePermissionService.permission.waitForRevalidation();
    }

    if (this.isOwner$.value === true) {
      return true;
    }

    const permissions = await (docId
      ? this.loadDocPermission(docId)
      : this.loadWorkspacePermission());

    return permissions[action as keyof typeof permissions] ?? false;
  }

  revalidateCan<T extends WorkspacePermissionActions | DocPermissionActions>(
    _action: T,
    ...args: T extends DocPermissionActions ? [string] : []
  ) {
    // revalidate workspace permission if it's not initialized
    if (this.isOwner$.value === null) {
      this.workspacePermissionService.permission.revalidate();
    }

    const docId = args[0];
    if (!docId && this.isOwner$.value === true) {
      // Owner workspace permissions are not overridable.
      return;
    }

    // revalidate permission
    if (docId) {
      this.revalidateDocPermission(docId);
    } else {
      this.revalidateWorkspacePermission();
    }
  }

  revalidateCachedPermissions(options?: {
    forceWorkspace?: boolean;
    docIds?: string[];
  }) {
    if (this.isOwner$.value === null) {
      this.workspacePermissionService.permission.revalidate();
    }

    if (options?.forceWorkspace || this.isOwner$.value !== true) {
      this.revalidateWorkspacePermission();
    }

    this.revalidateDocPermissions(options?.docIds ?? []);
  }

  getCachedDocPermission(
    action: DocPermissionActions,
    docId: string
  ): boolean | undefined {
    return (
      this.docPermissions$.value[docId]?.[action] ??
      this.guardStore.getCachedDocPermissions(docId)?.[action]
    );
  }

  private readonly revalidateWorkspacePermission = effect(
    exhaustMapWithTrailing(() =>
      fromPromise(() => this.guardStore.getWorkspacePermissions()).pipe(
        tap(permissions => {
          this.workspacePermissions$.next(permissions);
        })
      )
    )
  );

  private revalidateDocPermission(docId: string) {
    this.revalidateDocPermissions([docId]);
  }

  private revalidateDocPermissions(docIds: string[]) {
    const ids = Array.from(new Set(docIds.filter(Boolean)));
    void this.applyCachedDocPermissions(ids);
    void this.refreshDocPermissions(ids);
  }

  private async refreshDocPermissions(docIds: string[]): Promise<void> {
    const ids = Array.from(new Set(docIds.filter(Boolean)));
    if (ids.length === 0) {
      return;
    }

    const permissionsByDocId = await this.guardStore.getDocPermissionsBatch(
      ids,
      { force: true }
    );
    this.docPermissions$.next({
      ...this.docPermissions$.value,
      ...permissionsByDocId,
    });
  }

  private readonly loadWorkspacePermission = async () => {
    if (this.workspaceService.workspace.flavour === 'local') {
      return {} as Record<WorkspacePermissionActions, boolean>;
    }
    if (this.workspaceService.workspace.openOptions.isSharedMode) {
      return {};
    }
    const permissions = await this.guardStore.getWorkspacePermissions();
    this.workspacePermissions$.next(permissions);
    return permissions;
  };

  private readonly loadDocPermission = async (docId: string) => {
    const activeLoad = this.docPermissionLoads.get(docId);
    if (activeLoad) {
      return activeLoad;
    }

    const load = this.loadDocPermissionOnce(docId).finally(() => {
      if (this.docPermissionLoads.get(docId) === load) {
        this.docPermissionLoads.delete(docId);
      }
    });
    this.docPermissionLoads.set(docId, load);
    return load;
  };

  private readonly loadDocPermissionOnce = async (
    docId: string
  ): Promise<Record<DocPermissionActions, boolean>> => {
    if (this.workspaceService.workspace.flavour === 'local') {
      return {} as Record<DocPermissionActions, boolean>;
    }
    if (this.workspaceService.workspace.openOptions.isSharedMode) {
      return {} as Record<DocPermissionActions, boolean>;
    }
    await this.guardStore.hydrateLocalPermissionSnapshot();
    const cached = this.guardStore.getCachedDocPermissions(docId);
    if (cached) {
      this.docPermissions$.next({
        ...this.docPermissions$.value,
        [docId]: cached,
      });
      return cached;
    }
    const permissions = await this.guardStore.getDocPermissions(docId);
    this.docPermissions$.next({
      ...this.docPermissions$.value,
      [docId]: permissions,
    });
    return permissions;
  };

  private readonly applyCachedDocPermissions = async (docIds: string[]) => {
    await this.guardStore.hydrateLocalPermissionSnapshot();
    const nextPermissions = { ...this.docPermissions$.value };
    for (const docId of docIds) {
      const cached = this.guardStore.getCachedDocPermissions(docId);
      if (cached) {
        nextPermissions[docId] = cached;
      }
    }
    this.docPermissions$.next(nextPermissions);
  };

  override dispose() {
    this.permissionsChangedSubscription?.unsubscribe();
    this.revalidateWorkspacePermission.unsubscribe();
  }
}
